import { __ } from '@wordpress/i18n';
import { 
    Button,
    PanelBody,
    TextControl,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    IconButton,
    Flex,
    FlexItem,
    FlexBlock,
    Tooltip,
} from '@wordpress/components';
import { RichText } from '@wordpress/block-editor';
import { useState, useRef } from '@wordpress/element';
import { 
    dragHandle,
    plus,
    trash,
    arrowUp,
    arrowDown
} from '@wordpress/icons';
// import './style.scss';

const RepeaterField = ({ 
    label, 
    value, 
    onChange, 
    placeholder,
    type = 'text',
    component
}) => {
    if (type === 'richtext') {
        return (
            <RichText
                tagName="div"
                value={value}
                onChange={onChange}
                placeholder={placeholder}
            />
        );
    }

    if (type === 'icon' && component) {
        const IconComponent = component;
        return (
            <div className="repeater-field-icon kanews-control-item">
                <label>{label}</label>
                <IconComponent
                    value={value}
                    onChange={onChange}
                />
            </div>
        );
    }

    return (
        <TextControl
            label={label}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
        />
    );
};

const IconButtonWithTooltip = ({ icon, label, onClick, className }) => {
    return (
        <Tooltip text={label}>
            <IconButton
                icon={icon}
                onClick={onClick}
                aria-label={label}
                className={className}
            />
        </Tooltip>
    );
};

const RepeaterItem = ({ 
    item, 
    index, 
    fields, 
    onChange, 
    onRemove,
    onMoveUp,
    onMoveDown,
    isFirst,
    isLast,
    onDragStart,
    onDragOver,
    onDragEnd,
    isDragging
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const itemRef = useRef(null);
    
    const toggleOpen = (e) => {
        // Prevent toggle if clicking on a button
        if (e.target.closest('button')) {
            return;
        }
        setIsOpen(!isOpen);
    };

    const handleDragStart = (e) => {
        e.dataTransfer.setData('text/plain', index);
        onDragStart(index);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        onDragOver(index);
    };

    const handleDragEnd = () => {
        onDragEnd();
    };

    return (
        <Card 
            className={`repeater-item ${isDragging ? 'is-dragging' : ''}`}
            ref={itemRef}
            draggable
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
        >
            <CardHeader 
                onClick={toggleOpen}
                className="repeater-item-header"
            >
                <Flex align="center">
                    <FlexItem>
                        <IconButtonWithTooltip
                            icon={dragHandle}
                            label={__('Sürükle', 'kanews')}
                            className="drag-handle"
                        />
                    </FlexItem>
                    <FlexBlock>
                        <div className="repeater-item-title">
                            {item[fields[0]?.name] || __('Yeni Öğe', 'kanews')}
                        </div>
                    </FlexBlock>
                    <FlexItem>
                        <div className="repeater-item-actions">
                            {!isFirst && (
                                <IconButtonWithTooltip
                                    icon={arrowUp}
                                    label={__('Yukarı Taşı', 'kanews')}
                                    onClick={() => onMoveUp(index)}
                                    className="move-up-button"
                                />
                            )}
                            {!isLast && (
                                <IconButtonWithTooltip
                                    icon={arrowDown}
                                    label={__('Aşağı Taşı', 'kanews')}
                                    onClick={() => onMoveDown(index)}
                                    className="move-down-button"
                                />
                            )}
                            <IconButtonWithTooltip
                                icon={trash}
                                label={__('Sil', 'kanews')}
                                onClick={() => onRemove(index)}
                                className="delete-button is-destructive"
                            />
                        </div>
                    </FlexItem>
                </Flex>
            </CardHeader>
            {isOpen && (
                <CardBody>
                    {fields.map((field) => (
                        <RepeaterField
                            key={field.name}
                            label={field.label}
                            value={item[field.name]}
                            onChange={(value) => onChange(index, field.name, value)}
                            placeholder={field.placeholder}
                            type={field.type}
                            component={field.component}
                        />
                    ))}
                </CardBody>
            )}
        </Card>
    );
};

const Repeater = ({
    title,
    fields,
    items,
    onAdd,
    onRemove,
    onChange,
    onReorder,
    addButtonText,
    emptyMessage,
}) => {
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [draggedOverIndex, setDraggedOverIndex] = useState(null);

    const handleMoveUp = (index) => {
        if (index > 0) {
            onReorder(index, index - 1);
        }
    };

    const handleMoveDown = (index) => {
        if (index < items.length - 1) {
            onReorder(index, index + 1);
        }
    };

    const handleDragStart = (index) => {
        setDraggedIndex(index);
    };

    const handleDragOver = (index) => {
        if (draggedIndex !== null && draggedIndex !== index) {
            setDraggedOverIndex(index);
        }
    };

    const handleDragEnd = () => {
        if (draggedIndex !== null && draggedOverIndex !== null) {
            onReorder(draggedIndex, draggedOverIndex);
        }
        setDraggedIndex(null);
        setDraggedOverIndex(null);
    };

    return (
 
            <div className="repeater-container">
                {items.length > 0 ? (
                    items.map((item, index) => (
                        <RepeaterItem
                            key={item.uniqueId || index}
                            item={item}
                            index={index}
                            fields={fields}
                            onChange={onChange}
                            onRemove={onRemove}
                            onMoveUp={handleMoveUp}
                            onMoveDown={handleMoveDown}
                            isFirst={index === 0}
                            isLast={index === items.length - 1}
                            onDragStart={handleDragStart}
                            onDragOver={handleDragOver}
                            onDragEnd={handleDragEnd}
                            isDragging={index === draggedIndex}
                        />
                    ))
                ) : (
                    <div className="repeater-empty">
                        {emptyMessage}
                    </div>
                )}
                <Button
                    isPrimary
                    onClick={onAdd}
                    className="components-button editor-post-publish-button editor-post-publish-button__button is-primary"
                    icon={plus}
                >
                    {addButtonText}
                </Button>
            </div>
    );
};

export default Repeater; 